import type { CarrierServiceOption } from '@/lib/api/carrier-shipment-api'

import { Badge } from '@/components/ui/badge'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Viettel Post service the customer picked on the web / Mini App order. */
export interface CustomerShippingService {
    code: string
    name: string
}

interface Props {
    services: CarrierServiceOption[] | null
    selected: string
    onSelect: (serviceCode: string) => void
    customerService?: CustomerShippingService
}

/** Staff must tell the customer when the booked service is not theirs. */
function customerServiceNotice(
    services: CarrierServiceOption[],
    selected: string,
    customer: CustomerShippingService | undefined,
): string | null {
    if (!customer) return null
    if (!services.some((service) => service.service_code === customer.code))
        return m.carrier_form_customer_service_unavailable({
            name: customer.name,
        })
    if (selected && selected !== customer.code)
        return m.carrier_form_customer_service_changed({ name: customer.name })
    return null
}

export function CarrierServiceOptions({
    services,
    selected,
    onSelect,
    customerService,
}: Props) {
    if (services === null) return null
    const notice = customerServiceNotice(services, selected, customerService)
    const noticeLine = notice ? (
        <p className="text-warning text-sm" role="status">
            {notice}
        </p>
    ) : null
    if (services.length === 0) {
        return (
            <>
                <p className="text-destructive text-sm">
                    {m.carrier_form_no_services()}
                </p>
                {noticeLine}
            </>
        )
    }
    return (
        <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
                {m.carrier_form_service()}
            </legend>
            {services.map((service) => (
                <label
                    key={service.service_code}
                    className="flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm"
                    htmlFor={`carrier-service-${service.service_code}`}
                >
                    <input
                        id={`carrier-service-${service.service_code}`}
                        type="radio"
                        name="carrier-service"
                        value={service.service_code}
                        checked={selected === service.service_code}
                        onChange={() => onSelect(service.service_code)}
                    />
                    <span className="flex-1">
                        {service.service_name}
                        {customerService?.code === service.service_code ? (
                            <Badge variant="outline" className="ml-2">
                                {m.carrier_form_customer_service_tag()}
                            </Badge>
                        ) : null}
                    </span>
                    <span className="font-medium">
                        {formatVnd(service.fee)}
                    </span>
                    <span className="text-muted-foreground text-xs">
                        {m.carrier_form_lead_time()} {service.lead_time}
                    </span>
                </label>
            ))}
            {noticeLine}
        </fieldset>
    )
}
